import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import EventCheckInScanner, { CheckInStatsBar } from '../../components/checkin/EventCheckInScanner';
import {
  bootstrapCheckInPortal,
  clearCheckInSession,
  fetchCheckInStats,
  getCheckInSession,
  getParishCheckInSession,
  saveCheckInSession,
  verifyCheckInPassword,
  type CheckInStats,
} from '../../services/checkInService';

const EventCheckInPortalPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const portalToken = searchParams.get('t') || '';
  const adminEventId = searchParams.get('eventId') || '';
  const isAdminMode = searchParams.get('admin') === '1';

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [eventTitle, setEventTitle] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#4b1b7a');
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [stats, setStats] = useState<CheckInStats | null>(null);
  const [resolvedEventId, setResolvedEventId] = useState<string | undefined>();

  const refreshStats = useCallback(async () => {
    try {
      const data = await fetchCheckInStats();
      setStats(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      setLoading(true);
      setError(null);
      clearCheckInSession();

      try {
        if (isAdminMode && adminEventId) {
          const session = await getParishCheckInSession(adminEventId);
          if (cancelled) return;
          setResolvedEventId(session.eventId);
          setEventTitle(session.eventTitle);
          setPrimaryColor(session.customTheme?.primaryColor || '#4b1b7a');
          setStats(session.stats || null);
          setAuthenticated(true);
          return;
        }

        if (!portalToken) {
          setError('Link de check-in inválido. Solicite o link ao organizador do evento.');
          return;
        }

        const boot = await bootstrapCheckInPortal(portalToken);
        if (cancelled) return;
        if (!boot.success) {
          setError(boot.message || 'Link inválido ou expirado.');
          return;
        }

        setEventTitle(boot.data.eventTitle);
        setPrimaryColor(boot.data.customTheme?.primaryColor || '#4b1b7a');
        setResolvedEventId(boot.data.eventId);

        const existing = getCheckInSession();
        if (existing) {
          setAuthenticated(true);
          await refreshStats();
        }
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Não foi possível carregar o portal de check-in.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void init();
    return () => {
      cancelled = true;
    };
  }, [adminEventId, isAdminMode, portalToken, refreshStats]);

  useEffect(() => {
    if (!authenticated) return;
    void refreshStats();
    const id = setInterval(() => void refreshStats(), 20000);
    return () => clearInterval(id);
  }, [authenticated, refreshStats]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordBusy(true);
    setError(null);
    try {
      const res = await verifyCheckInPassword(portalToken, password);
      if (res.success && res.data?.sessionToken) {
        saveCheckInSession(res.data.sessionToken);
        setAuthenticated(true);
        setStats(null);
        await refreshStats();
      } else {
        setError(res.message || 'Senha incorreta.');
      }
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Senha incorreta.');
    } finally {
      setPasswordBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !authenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!authenticated) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Stack spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <LockOutlinedIcon sx={{ fontSize: 40, color: primaryColor }} />
            <Typography variant="h6" fontWeight={800} textAlign="center">
              Check-in — {eventTitle}
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Digite a senha fornecida pelo organizador para acessar a tela de check-in.
            </Typography>
          </Stack>
          <Box component="form" onSubmit={handlePasswordSubmit}>
            <TextField
              fullWidth
              type="password"
              label="Senha do check-in"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              sx={{ mb: 2 }}
            />
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={passwordBusy || password.length < 4}
              sx={{ bgcolor: primaryColor, py: 1.25 }}
            >
              {passwordBusy ? 'Verificando...' : 'Entrar'}
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f4f6fb', pb: 4 }}>
      <Box sx={{ bgcolor: primaryColor, color: '#fff', py: 2.5, px: 2, mb: 2 }}>
        <Container maxWidth="sm">
          <Typography variant="overline" sx={{ opacity: 0.85, letterSpacing: 1 }}>
            Check-in
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {eventTitle}
          </Typography>
          {stats && (
            <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
              {stats.checkedInCount} de {stats.totalRegistrations} ({stats.checkInPercentage}%)
            </Typography>
          )}
        </Container>
      </Box>
      <Container maxWidth="sm">
        <CheckInStatsBar stats={stats} primaryColor={primaryColor} />
        <EventCheckInScanner
          eventId={resolvedEventId}
          parishMode={false}
          primaryColor={primaryColor}
          onStatsRefresh={refreshStats}
        />
      </Container>
    </Box>
  );
};

export default EventCheckInPortalPage;
